import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AttachmentUploadRejection } from '@app/api/api.helper';
import { TranslatePipe } from '@ngx-translate/core';
import { NoticeComponent } from '@sb/ui/notice';
import { UploadComponent, type FileValidationResult } from '@sb/ui/upload';
import { clsx } from 'clsx';
import { finalize } from 'rxjs';

import { DOCX_MIME_TYPE, SampleContractService } from '../services/sample-contract.service';

const DOCX_TYPES = ['.docx', DOCX_MIME_TYPE];

/** Step 1: pick the contract. The file leaves through `fileSelected`, this step never uploads it. */
@Component({
  selector: 'upload-step',
  templateUrl: './upload-step.component.html',
  imports: [NoticeComponent, TranslatePipe, UploadComponent],
  host: { '[class]': 'hostClasses' },
})
export class UploadStepComponent {
  hostClasses = clsx('flex w-full flex-col gap-md');

  isUploading = input(false);
  rejection = input<AttachmentUploadRejection | null>(null);

  fileSelected = output<File>();
  setUpManually = output();

  #sampleContract = inject(SampleContractService);
  #destroyRef = inject(DestroyRef);

  docxTypes = DOCX_TYPES;
  rejectedFile = signal(false);
  isLoadingSample = signal(false);
  sampleFailed = signal(false);

  trySample(): void {
    this.rejectedFile.set(false);
    this.sampleFailed.set(false);
    this.isLoadingSample.set(true);

    this.#sampleContract
      .fetch()
      .pipe(
        finalize(() => this.isLoadingSample.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: (sample) => this.fileSelected.emit(sample),
        error: () => this.sampleFailed.set(true),
      });
  }

  handleUpload({ validFiles, errors }: FileValidationResult): void {
    const [contract] = validFiles;

    this.rejectedFile.set(!contract || errors.invalidFileFound);
    this.sampleFailed.set(false);

    if (contract && !errors.invalidFileFound) {
      this.fileSelected.emit(contract);
    }
  }
}
